import type { MarketingRouterRule } from '~/types/marketingRouterRule';
import type { AuditLog } from '~/types/responses';

import { isoToSydneyDate } from '~/utils/dateFormatter';
import { isRecord } from '~/utils/isRecord';

import { getRuleChanges } from './marketingRouterChanges';

/**
 * svc-promo audits a marketing router row, not a rule: `entityId` is the
 * router id and the whole `rule_json` array travels as one before/after pair
 * (svc-promo `src/lib/audit/marketingRouterAudit.ts`). A router covers a whole
 * brand/region, so an event on it may be about any of its rules.
 *
 * This module turns that array pair back into per-rule changes, so a rule page
 * can show only the events that touched the rule on screen and describe them
 * in the words the form uses.
 */

type StoredRule = MarketingRouterRule & { enabled?: boolean };

export type MarketingRouterRuleAuditChange = {
  ruleId: string;
  ruleName: string;
  kind: 'added' | 'removed' | 'updated';
  /** Empty for added/removed rules, where the kind is the whole story. */
  lines: Array<string>;
};

/**
 * The audit payload is historical JSON that no schema guards: rules written
 * before a field existed read back missing it, and match fields written before
 * they became lists read back as plain strings.
 *
 * The hook that feeds the rest of the admin normalizes a live API response and
 * can assume the current contract; this cannot, so it fills every field
 * `getRuleChanges` reads rather than sharing that normalizer.
 */
function normalizeStoredRule(value: unknown): StoredRule | null {
  if (!isRecord(value) || typeof value.id !== 'string' || value.id.trim() === '') {
    return null;
  }

  const match = isRecord(value.match) ? value.match : {};

  return {
    id: value.id,
    name: typeof value.name === 'string' ? value.name : '',
    ...(typeof value.enabled === 'boolean' ? { enabled: value.enabled } : {}),
    significance_metrics: Array.isArray(value.significance_metrics)
      ? (value.significance_metrics.filter(
          (metric) => typeof metric === 'string',
        ) as StoredRule['significance_metrics'])
      : [],
    start_date: typeof value.start_date === 'string' ? isoToSydneyDate(value.start_date) : '',
    end_date: typeof value.end_date === 'string' ? isoToSydneyDate(value.end_date) : '',
    match: match as StoredRule['match'],
    variants: Array.isArray(value.variants) ? (value.variants.filter(isRecord) as StoredRule['variants']) : [],
  };
}

function normalizeStoredRules(value: unknown): Array<StoredRule> {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((rule) => {
    const normalized = normalizeStoredRule(rule);
    return normalized ? [normalized] : [];
  });
}

function getRuleJsonChange(log: AuditLog): { before: Array<StoredRule>; after: Array<StoredRule> } | null {
  const change = log.model?.diff?.updated?.rule_json;
  if (!isRecord(change)) {
    return null;
  }

  // svc-promo writes `before`/`after`; svc-audit's own shape uses `old`/`new`.
  const before = 'before' in change ? change.before : change.old;
  const after = 'after' in change ? change.after : change.new;

  return {
    before: normalizeStoredRules(before),
    after: normalizeStoredRules(after),
  };
}

/** Unset counts as enabled, matching how svc-promo reads a stored rule. */
function isEnabled(rule: StoredRule): boolean {
  return rule.enabled ?? true;
}

function describeRuleName(rule: StoredRule): string {
  return rule.name.trim() || rule.id;
}

/**
 * Every rule an event changed, in the order the router stores them.
 *
 * Rules are matched by id: unlike the variants inside a rule, rules can be
 * added and removed, so position is not identity here.
 */
export function getMarketingRouterRuleAuditChanges(log: AuditLog): Array<MarketingRouterRuleAuditChange> {
  const ruleJson = getRuleJsonChange(log);
  if (!ruleJson) {
    return [];
  }

  const beforeById = new Map(ruleJson.before.map((rule) => [rule.id, rule]));
  const afterById = new Map(ruleJson.after.map((rule) => [rule.id, rule]));

  const added: Array<MarketingRouterRuleAuditChange> = [];
  const updated: Array<MarketingRouterRuleAuditChange> = [];

  ruleJson.after.forEach((after) => {
    const before = beforeById.get(after.id);

    if (!before) {
      added.push({ ruleId: after.id, ruleName: describeRuleName(after), kind: 'added', lines: [] });
      return;
    }

    const lines = getRuleChanges(before, after);
    if (isEnabled(before) !== isEnabled(after)) {
      lines.unshift(isEnabled(after) ? 'Enabled' : 'Disabled');
    }

    if (lines.length > 0) {
      updated.push({ ruleId: after.id, ruleName: describeRuleName(after), kind: 'updated', lines });
    }
  });

  const removed = ruleJson.before
    .filter((before) => !afterById.has(before.id))
    .map(
      (before): MarketingRouterRuleAuditChange => ({
        ruleId: before.id,
        ruleName: describeRuleName(before),
        kind: 'removed',
        lines: [],
      }),
    );

  return [...added, ...updated, ...removed];
}

/**
 * Whether an event says anything about one rule.
 *
 * An event with no `rule_json` diff changed the router itself — its region,
 * brand or date window — which applies to every rule the router holds, so it
 * counts as touching this one.
 */
export function marketingRouterAuditEventTouchesRule(log: AuditLog, ruleId: string): boolean {
  const changes = getMarketingRouterRuleAuditChanges(log);
  if (changes.length > 0) {
    return changes.some((change) => change.ruleId === ruleId);
  }

  return !getRuleJsonChange(log);
}

const KIND_PREFIX: Record<MarketingRouterRuleAuditChange['kind'], string> = {
  added: 'Added rule',
  removed: 'Removed rule',
  updated: '',
};

function describeChange(change: MarketingRouterRuleAuditChange, includeRuleName: boolean): string {
  if (change.kind !== 'updated') {
    return `${KIND_PREFIX[change.kind]} “${change.ruleName}”`;
  }

  const lines = change.lines.join('; ');
  return includeRuleName ? `“${change.ruleName}”: ${lines}` : lines;
}

/**
 * One line describing what an event did, in the form's own words.
 *
 * Scoped to `ruleId` when given, so a rule page does not report edits to the
 * other rules that share its router. Returns null when there is nothing
 * router-specific to say and the caller should fall back to the generic
 * audit summary — which for these events reads only "Changed Rule Json".
 */
export function summarizeMarketingRouterAuditEvent(log: AuditLog, ruleId?: string): string | null {
  const changes = getMarketingRouterRuleAuditChanges(log);
  const scoped = ruleId ? changes.filter((change) => change.ruleId === ruleId) : changes;

  if (scoped.length === 0) {
    return null;
  }

  return scoped.map((change) => describeChange(change, !ruleId || scoped.length > 1)).join(' · ');
}
